import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Trend Insights Component
 * Displays overall trend summary, growth rates, and key insights
 */
const TrendInsights = ({
  summary,
  metric = 'revenue',
  format = 'currency'
}) => {
  if (!summary) {
    return null;
  }

  const overallTrend = summary.overall_trend || 'stable';
  const dataPoints = summary.data_points || 0;

  // Format value based on type
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';

    switch (format) {
      case 'currency':
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'number':
      default:
        return value.toLocaleString();
    }
  };

  // Get trend icon and color
  const getTrendConfig = (trend) => {
    switch (trend) {
      case 'increasing':
        return {
          icon: TrendingUp,
          color: 'text-green-600',
          bgColor: 'bg-green-50 dark:bg-green-950',
          borderColor: 'border-green-200 dark:border-green-800',
          label: 'Increasing',
          badgeVariant: 'default'
        };
      case 'decreasing':
        return {
          icon: TrendingDown,
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-950',
          borderColor: 'border-red-200 dark:border-red-800',
          label: 'Decreasing',
          badgeVariant: 'destructive'
        };
      default:
        return {
          icon: Minus,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/50',
          borderColor: 'border-border',
          label: 'Stable',
          badgeVariant: 'secondary'
        };
    }
  };

  const trendConfig = getTrendConfig(overallTrend);
  const TrendIcon = trendConfig.icon;

  // Calculate key metrics based on metric type
  const getMetricKey = () => {
    switch (metric) {
      case 'revenue':
        return {
          total: 'total_revenue',
          average: 'average_revenue',
          max: 'max_revenue',
          min: 'min_revenue'
        };
      case 'impressions':
        return {
          total: 'total_impressions',
          average: 'average_impressions',
          max: 'max_impressions',
          min: 'min_impressions'
        };
      case 'clicks':
        return {
          total: 'total_clicks',
          average: 'average_clicks',
          max: 'max_clicks',
          min: 'min_clicks'
        };
      case 'ctr':
        return {
          total: null,
          average: 'average_ctr',
          max: null,
          min: null
        };
      case 'fill_rate':
        return {
          total: null,
          average: 'average_fill_rate',
          max: null,
          min: null
        };
      case 'completion_rate':
        return {
          total: null,
          average: 'average_completion_rate',
          max: null,
          min: null
        };
      case 'active_users':
        return {
          total: 'total_active_users',
          average: null,
          max: null,
          min: null
        };
      case 'sessions':
        return {
          total: 'total_sessions',
          average: null,
          max: null,
          min: null
        };
      default:
        return {
          total: `total_${metric}`,
          average: `average_${metric}`,
          max: `max_${metric}`,
          min: `min_${metric}`
        };
    }
  };

  const metricKeys = getMetricKey();
  const total = metricKeys.total ? (summary[metricKeys.total] || 0) : null;
  const average = metricKeys.average ? (summary[metricKeys.average] || 0) : null;
  const max = metricKeys.max ? (summary[metricKeys.max] || 0) : null;
  const min = metricKeys.min ? (summary[metricKeys.min] || 0) : null;

  // Calculate variance
  const variance = max !== null && min !== null && max > 0 && min > 0 ? ((max - min) / max) * 100 : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm sm:text-base">Trend Analysis</CardTitle>
          <Badge variant={trendConfig.badgeVariant} className="flex items-center gap-1 text-xs">
            <TrendIcon className="h-3 w-3" />
            {trendConfig.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2 sm:space-y-3 lg:space-y-4">
          {/* Overall Trend Indicator */}
          <div className={cn(
            "p-2 sm:p-3 lg:p-4 rounded-lg border",
            trendConfig.bgColor,
            trendConfig.borderColor
          )}>
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <span className="text-xs sm:text-sm font-medium text-muted-foreground">Overall Trend</span>
              <TrendIcon className={cn("h-4 w-4 sm:h-5 sm:w-5", trendConfig.color)} />
            </div>
            <div className="text-lg sm:text-xl lg:text-2xl font-bold">{trendConfig.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5 sm:mt-1">
              Based on {dataPoints} data point{dataPoints !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {total !== null && (
              <div className="p-2 sm:p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-0.5">Total</div>
                <div className="text-base sm:text-lg font-semibold">{formatValue(total)}</div>
              </div>
            )}
            {average !== null && (
              <div className="p-2 sm:p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-0.5">Average</div>
                <div className="text-base sm:text-lg font-semibold">{formatValue(average)}</div>
              </div>
            )}
            {max !== null && (
              <div className="p-2 sm:p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-0.5">Peak</div>
                <div className="text-base sm:text-lg font-semibold flex items-center gap-1">
                  <ArrowUp className="h-3 w-3 text-green-600" />
                  {formatValue(max)}
                </div>
              </div>
            )}
            {min !== null && (
              <div className="p-2 sm:p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-0.5">Low</div>
                <div className="text-base sm:text-lg font-semibold flex items-center gap-1">
                  <ArrowDown className="h-3 w-3 text-red-600" />
                  {formatValue(min)}
                </div>
              </div>
            )}
          </div>

          {/* Variance Indicator */}
          {variance > 0 && (
            <div className="p-2 sm:p-3 bg-background rounded-lg border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                  <span className="text-xs sm:text-sm font-medium">Variability</span>
                </div>
                <span className="text-xs sm:text-sm font-semibold">{variance.toFixed(1)}%</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 sm:mt-1">
                Difference between peak and low values
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendInsights;
